// Small building blocks shared by the pages.

import { Badge, type BadgeColor, Button, cn } from '@waforms/ui';
import { Check, CircleAlert, Copy, Info, TriangleAlert } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { FormLifecycle } from '../../shared/api-types.ts';
import { charCount } from '../../shared/limits.ts';

const LIFECYCLE: Record<FormLifecycle, { label: string; color: BadgeColor; hint: string }> = {
  draft: { label: 'Draft', color: 'slate', hint: 'Not published yet: it cannot be sent.' },
  published: { label: 'Published', color: 'teal', hint: 'Live on WhatsApp.' },
  changes: {
    label: 'Unpublished changes',
    color: 'amber',
    hint: 'Customers still get the last published version until you publish again.',
  },
  blocked: {
    label: 'Blocked by Meta',
    color: 'ruby',
    hint: 'WhatsApp will not open this form. Check it in WhatsApp Manager.',
  },
  throttled: {
    label: 'Throttled by Meta',
    color: 'amber',
    hint: 'Meta limits how often this form can be sent.',
  },
  archived: { label: 'Archived', color: 'slate', hint: 'No longer sent; its submissions are kept.' },
};

export function StatusBadge({ lifecycle }: { lifecycle: FormLifecycle }) {
  const entry = LIFECYCLE[lifecycle];
  return (
    <Badge color={entry.color} title={entry.hint}>
      {entry.label}
    </Badge>
  );
}

export const lifecycleHint = (lifecycle: FormLifecycle): string => LIFECYCLE[lifecycle].hint;

type CalloutTone = 'info' | 'warning' | 'error' | 'success';

const TONES: Record<CalloutTone, { box: string; icon: ReactNode }> = {
  info: { box: 'bg-n-alpha-1 text-n-slate-12', icon: <Info className="size-4 text-n-slate-11" /> },
  warning: {
    box: 'bg-n-amber-9/10 text-n-slate-12',
    icon: <TriangleAlert className="size-4 text-n-amber-11" />,
  },
  error: { box: 'bg-n-ruby-9/10 text-n-slate-12', icon: <CircleAlert className="size-4 text-n-ruby-11" /> },
  success: { box: 'bg-n-teal-9/10 text-n-slate-12', icon: <Check className="size-4 text-n-teal-11" /> },
};

export function Callout({
  tone = 'info',
  title,
  children,
  action,
  className,
}: {
  tone?: CalloutTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : undefined}
      className={cn('flex items-start gap-3 rounded-xl px-4 py-3 text-body-main', TONES[tone].box, className)}
    >
      <span className="mt-0.5 shrink-0" aria-hidden="true">
        {TONES[tone].icon}
      </span>
      <div className="flex min-w-0 grow flex-col gap-1">
        {title && <p className="m-0 text-heading-3">{title}</p>}
        {children && <div className="text-n-slate-11 [&_p]:m-0">{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/** Copies `value` to the clipboard and says so for two seconds. */
export function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  return (
    <Button
      size="xs"
      variant="faded"
      color="slate"
      icon={copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => setCopied(false), 2000);
        });
      }}
    >
      {copied ? 'Copied' : label}
    </Button>
  );
}

/** "12/20", red when over the limit. */
export function CharCount({ value, max }: { value: string | null | undefined; max: number }) {
  const length = charCount((value ?? '').trim());
  return (
    <span
      className={cn('text-label-small tabular-nums', length > max ? 'text-n-ruby-11' : 'text-n-slate-10')}
    >
      {length}/{max}
    </span>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl bg-n-solid-2 px-4 py-3 outline outline-1 -outline-offset-1 outline-n-container">
      <span className="text-label-small text-n-slate-11">{label}</span>
      <span className="text-heading-1 tabular-nums text-n-slate-12">{value}</span>
      {hint && <span className="text-label-small text-n-slate-10">{hint}</span>}
    </div>
  );
}
